import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { calendarKeys } from 'core/utils/query-key-factory';
import { Identity } from 'core/types';
import {
	CalendarFilter,
	CalendarViewTypes,
	StatusFilter,
	EventTypeFilter,
} from '../types';
import { FILTER_STATUSES } from '../constants';

export type CalendarState = {
	statuses: StatusFilter[];
	memberIds: Identity['uuid'][];
	eventTypes: EventTypeFilter[];
	view?: CalendarViewTypes;
	displayed: string; // ISO string for DateTime
	agendaFilterOption?: string; // 'monthly', 'weekly', 'next_7_days', 'next_30_days', 'next_90_days', 'custom-range'
	agendaCustomDates?: {
		startDate: string; // ISO string for DateTime
		endDate: string; // ISO string for DateTime
	};
};

const getDefaultState = (
	timezone: string,
	defaultDisplayed?: DateTime
): CalendarState => ({
	statuses: [
		FILTER_STATUSES.HELP_NEEDED as StatusFilter,
		FILTER_STATUSES.NEEDS_MET as StatusFilter,
		FILTER_STATUSES.OCCASIONS as StatusFilter,
	],
	memberIds: [],
	eventTypes: [],
	view: undefined,
	displayed:
		defaultDisplayed?.toISO() ||
		DateTime.local({ zone: timezone }).toISO() ||
		'',
});

export const useCalendarState = (
	calendarId: string,
	timezone: string,
	defaultDisplayed?: DateTime
) => {
	const queryClient = useQueryClient();
	const stateKey = calendarKeys.state(calendarId);

	const defaultState = useMemo(
		() => getDefaultState(timezone, defaultDisplayed),
		[timezone, defaultDisplayed]
	);

	// Use useQuery to get reactive state from cache
	const { data: state = defaultState } = useQuery<CalendarState>({
		queryKey: stateKey,
		queryFn: () => {
			const cached = queryClient.getQueryData<CalendarState>(stateKey);
			return cached || defaultState;
		},
		enabled: true,
		staleTime: Infinity,
		gcTime: Infinity,
	});

	const setState = useCallback(
		(updater: (prev: CalendarState) => CalendarState) => {
			queryClient.setQueryData<CalendarState>(stateKey, prev => {
				const current = prev || defaultState;
				return updater(current);
			});
		},
		[stateKey, defaultState, queryClient]
	);

	const setFilter = useCallback(
		(filter: CalendarFilter) => {
			setState(prev => ({
				...prev,
				statuses: filter.statuses,
				memberIds: filter.memberIds,
				eventTypes: filter.eventTypes,
			}));
		},
		[setState]
	);

	const setView = useCallback(
		(view: CalendarViewTypes | undefined) => {
			setState(prev => ({
				...prev,
				view,
			}));
		},
		[setState]
	);

	const setDisplayed = useCallback(
		(displayed: DateTime) => {
			setState(prev => ({
				...prev,
				displayed: displayed.toISO() || '',
			}));
		},
		[setState]
	);

	const setAgendaFilterOption = useCallback(
		(filterOption: string | undefined) => {
			setState(prev => ({
				...prev,
				agendaFilterOption: filterOption,
			}));
		},
		[setState]
	);

	const setAgendaCustomDates = useCallback(
		(dates: { startDate: DateTime; endDate: DateTime } | undefined) => {
			setState(prev => ({
				...prev,
				agendaCustomDates: dates
					? {
							startDate: dates.startDate.toISO() || '',
							endDate: dates.endDate.toISO() || '',
						}
					: undefined,
			}));
		},
		[setState]
	);

	return {
		filter: {
			statuses: state.statuses,
			memberIds: state.memberIds,
			eventTypes: state.eventTypes,
		},
		view: state.view,
		displayed: state.displayed
			? DateTime.fromISO(state.displayed, { zone: timezone })
			: DateTime.local({ zone: timezone }),
		agendaFilterOption: state.agendaFilterOption,
		agendaCustomDates: state.agendaCustomDates
			? {
					startDate: DateTime.fromISO(
						state.agendaCustomDates.startDate,
						{
							zone: timezone,
						}
					),
					endDate: DateTime.fromISO(state.agendaCustomDates.endDate, {
						zone: timezone,
					}),
				}
			: undefined,
		setFilter,
		setView,
		setDisplayed,
		setAgendaFilterOption,
		setAgendaCustomDates,
	};
};
